import {Component, Input} from '@angular/core';
import {Router, ActivatedRoute} from '@angular/router';
import {ListViewItem} from '@domain/common/list-view/list-view-item';
import {FournisseursService} from "@services/admin/facture/fournisseurs.service";
import {TranslateService} from '@ngx-translate/core';
import {Contrat, TypeDebit, TypeImport} from "@domain/contrat/contrat";

/**
 * Composant d'affichage d'un item contrat dans une liste
 *
 * @author Yannick Gauthier
 * @date 14/08/2025
 */
@Component({
    selector: 'factures-fournisseurs-contrat-item',
    templateUrl: './factures-fournisseurs-contrat-item.component.html',
    host: {'data-test-id': 'factures-fournisseurs-contrat-item',
        'class': 'clickable'
    }
})
export class FacturesFournisseursContratItemComponent extends ListViewItem<any> {
    /** Données du contrat */
    @Input() data: Contrat;

    /** Affichage/masquage de l'élément */
    @Input() toggleItem: (data: any) => void;

    /** Indique si le contrat est actif en fonction des dates */
    get isActif(): boolean {
        const today = new Date();
        
        //Contrat inactif si la date de début est dans le futur
        if (this.data.dateDebut && new Date(this.data.dateDebut) > today) {
            return false;
        }
        
        //Contrat inactif si la date de fin est dans le passé
        if (this.data.dateFin && new Date(this.data.dateFin) < today) {
            return false;
        }
        
        //Dans tous les autres cas, le contrat est actif
        return true;
    }

    /**
     * Constructeur
     *
     * @param router Service de routage
     * @param activatedRoute Route activée
     * @param fournisseurService Service de gestion des fournisseurs
     * @param translateService Service de traduction
     */
    constructor(private router: Router,
                private activatedRoute: ActivatedRoute,
                public fournisseurService: FournisseursService,
                private translateService: TranslateService) {
        super();
    }

    /**
     * Méthode appelée lors du clic sur un contrat
     * Navigation vers la page de détail du contrat
     */
    onItemClick() {
        //Récupération de l'ID du fournisseur depuis la route parente
        const idFournisseur = this.activatedRoute.parent?.snapshot.params['id'];

        //Navigation vers la page de détail du contrat avec l'ID du fournisseur en paramètre
        this.router.navigate(['Admin/Frais/Factures/Contrat', this.data.idContrat], {
            queryParams: { idFournisseur: idFournisseur }
        });
    }

    /**
     * Retourne le libellé du type de contrat
     */
    getLibelleTypeContrat(): string {
        //Si type débit est SOCIETE
        if (this.data.typeDebit === TypeDebit.SOCIETE) {
            //Si type import est CARTE
            if (this.data.typeImport === TypeImport.CARTE) {
                return this.translateService.instant('admin.frais.fournisseurs.contrat.type.societeCarte');
            } else {
                return this.translateService.instant('admin.frais.fournisseurs.contrat.type.societeFacture');
            }
        //Si type débit est COLLAB
        } else if (this.data.typeDebit === TypeDebit.COLLAB) {
            //Si type import est CARTE
            if (this.data.typeImport === TypeImport.CARTE) {
                return this.translateService.instant('admin.frais.fournisseurs.contrat.type.collabCarte');
            } else {
                return this.translateService.instant('admin.frais.fournisseurs.contrat.type.collabFacture');
            }
        } else {
            return '';
        }
    }
}